import { Locale } from "./config";

import enAbout from "../../messages/en/about.json";
import enAppReview from "../../messages/en/appReview.json";
import enAuth from "../../messages/en/auth.json";
import enCaseTracker from "../../messages/en/caseTracker.json";
import enCommon from "../../messages/en/common.json";
import enCommunity from "../../messages/en/community.json";
import enHome from "../../messages/en/home.json";
import enBooking from "../../messages/en/booking.json";
import enLawyers from "../../messages/en/lawyers.json";
import enMockInterview from "../../messages/en/mockInterview.json";
import enNav from "../../messages/en/nav.json";
import enStatusExplorer from "../../messages/en/statusExplorer.json";
import esAbout from "../../messages/es/about.json";
import esAppReview from "../../messages/es/appReview.json";
import esAuth from "../../messages/es/auth.json";
import esCaseTracker from "../../messages/es/caseTracker.json";
import esCommon from "../../messages/es/common.json";
import esCommunity from "../../messages/es/community.json";
import esHome from "../../messages/es/home.json";
import esBooking from "../../messages/es/booking.json";
import esLawyers from "../../messages/es/lawyers.json";
import esMockInterview from "../../messages/es/mockInterview.json";
import esNav from "../../messages/es/nav.json";
import esStatusExplorer from "../../messages/es/statusExplorer.json";

/**
 * Namespaces are one JSON file per page (plus `common` for chrome shared across
 * pages). Add a namespace here and it becomes available to useTranslations().
 *
 * Both locales are imported statically, so both ship in the shared bundle. With
 * two locales and marketing copy that is a few KB gzipped, which buys us pages
 * that need no getStaticProps -- most marketing pages here have no data
 * fetching at all, and adding it just to carry messages was the main reason
 * next-i18next was rejected (PRD D3).
 *
 * If the payload ever justifies it, the fix is to move loading into
 * getStaticProps per page and feed it through the same MessagesProvider; the
 * useTranslations() call sites do not change.
 */
const MESSAGES = {
  en: {
    about: enAbout,
    appReview: enAppReview,
    auth: enAuth,
    caseTracker: enCaseTracker,
    common: enCommon,
    community: enCommunity,
    home: enHome,
    lawyers: enLawyers,
    booking: enBooking,
    mockInterview: enMockInterview,
    nav: enNav,
    statusExplorer: enStatusExplorer,
  },
  es: {
    about: esAbout,
    appReview: esAppReview,
    auth: esAuth,
    caseTracker: esCaseTracker,
    common: esCommon,
    community: esCommunity,
    home: esHome,
    lawyers: esLawyers,
    booking: esBooking,
    mockInterview: esMockInterview,
    nav: esNav,
    statusExplorer: esStatusExplorer,
  },
} as const;

export type Namespace = keyof (typeof MESSAGES)["en"];

export type MessageTree = {
  [key: string]: string | MessageTree;
};

export function getMessages(locale: Locale, namespace: Namespace): MessageTree {
  return MESSAGES[locale][namespace] as MessageTree;
}

/**
 * Walks a dot path such as "meta.title". Returns undefined on a miss so the
 * caller decides how loud to be about it.
 */
export function resolveMessage(
  tree: MessageTree,
  key: string,
): string | undefined {
  let node: string | MessageTree | undefined = tree;

  for (const segment of key.split(".")) {
    if (typeof node !== "object" || node === null) return undefined;
    node = node[segment];
  }

  return typeof node === "string" ? node : undefined;
}
